import {
  ProvenanceTargetType,
  TargetType
} from 'components/attacks/create/create-attack-types'
import {
  buildLabelSelectionObject,
  K8sTargetingSelectionType
} from 'components/attacks/create/targets/kubernetes/kubernetes-attack-form-utils'
import { getTargetParamsKey } from 'components/attacks/create/targets/target-utils'
import { InfraTargetFormValues } from 'components/attacks/create/utils/infra-initial-form-state'
import { Client } from 'types/api/attacks/client'
import {
  AttackCommandConfiguration,
  AttackConfiguration,
  AttackSamplingConfiguration,
  AttackTargetingConfiguration,
  ContainerTargetingStrategy,
  HostTargetingStrategy,
  KubernetesTargetingStrategy,
  Names,
  SnakeCaseAttackCommandConfiguration,
  TargetingStrategy
} from 'types/api/attacks/details/targeting'
import { ProcessProvenance } from 'types/api/services/service'
import { InfraTargetingAutoCompleteSearchOption } from 'types/forms'
import { CommandArg, MetadataEntry } from 'types/metadata'
import { naturalSortBy, unique } from 'utils/list'
import { cliArgsToInfraCommandArgs } from 'utils/metadata'

export function createTargetingStrategyFromInfraForm(
  processProvenance: ProcessProvenance,
  target: InfraTargetFormValues,
  targetSelectionStrategy: string,
  targetType: TargetType,
  selectedTargets: string[]
): TargetingStrategy {
  const targetingStrategy: TargetingStrategy = {
    type: ProvenanceTargetType[processProvenance],
    isAll: Boolean(target.targetAll)
  }

  if (!target.targetAll) {
    if (targetSelectionStrategy === 'Random') {
      targetingStrategy[
        getTargetParamsKey(targetType, targetSelectionStrategy)
      ] = target.targetParams
    } else {
      targetingStrategy.ids = selectedTargets
    }
  }

  return targetingStrategy
}

export function getUniqueDeviceListFromClients(clients: Client[]) {
  return naturalSortBy(
    unique(
      clients
        .map((client) => client.network_interface_data?.interfaces)
        .filter((list) => list?.length > 0)
        .flatMap((interfaces) => {
          return interfaces.map((iface) => iface.name)
        })
    )
  )
}

export function generateAttackConfigurationSampling(
  target
): AttackSamplingConfiguration {
  if (target.exact || target.count) {
    return {
      type: 'Exact',
      count: target.exact
    }
  } else {
    return {
      type: 'Even',
      percent: target.percentage || 100
    }
  }
}

function generateHostAttackConfigurationTargets(
  target
): AttackTargetingConfiguration {
  const { targetingStrategyType, targetAll, targetParams, selectedObjects } =
    target

  const targetingStrategy: HostTargetingStrategy = {
    type: ProvenanceTargetType.ProcessData
  }

  if (targetAll) {
    targetingStrategy.isAll = targetAll
  } else {
    if (targetingStrategyType?.toLowerCase() === 'exact') {
      targetingStrategy.ids = [...selectedObjects]
    } else {
      targetingStrategy[
        getTargetParamsKey(
          ProvenanceTargetType.ProcessData,
          targetingStrategyType
        )
      ] = targetParams
    }
  }

  return [
    {
      targetingStrategy
    }
  ]
}

function generateContainerAttackConfigurationTargets(target) {
  const { targetingStrategyType, targetAll, targetParams, selectedObjects } =
    target

  const targetingStrategy: ContainerTargetingStrategy = {
    type: ProvenanceTargetType.Container
  }

  if (targetAll) {
    targetingStrategy.isAll = targetAll
  }

  if (targetingStrategyType?.toLowerCase() === 'exact') {
    targetingStrategy.ids = [...selectedObjects]
  } else {
    targetingStrategy[
      getTargetParamsKey(ProvenanceTargetType.Container, targetingStrategyType)
    ] = targetParams
  }

  return [
    {
      targetingStrategy
    }
  ]
}

function generateKubernetesAttackConfigurationTargets(
  target,
  impact,
  targets
): AttackTargetingConfiguration {
  const targetingStrategy: KubernetesTargetingStrategy = {
    type: ProvenanceTargetType.Kubernetes,
    isAll: false
  }

  if (impact.containerSelection?.selectionType) {
    targetingStrategy.containerSelection = {
      selectionType: impact.containerSelection.selectionType.toUpperCase(),
      containerNames: [...impact.containerSelection.containerNames]
    }
  }

  if (target.targetingSelectionType === K8sTargetingSelectionType.Selectors) {
    targetingStrategy.labelSelection = buildLabelSelectionObject(
      target.labelSelector,
      target.selectedClusterId,
      target.selectedNamespace
    )
  } else {
    const targetNames: Names[] = targets
      ?.filter((currentTarget) => target.selectedObjects.has(currentTarget.uid))
      .map(({ clusterId, namespace, kind, name }) => ({
        clusterId,
        kind,
        namespace,
        name
      }))

    targetingStrategy.names = targetNames
  }

  return [
    {
      targetingStrategy
    }
  ]
}

export function generateAttackConfigurationTargets(
  targetType: TargetType,
  target,
  impact,
  targets
) {
  if (targetType === TargetType.Hosts) {
    return generateHostAttackConfigurationTargets(target)
  }

  if (targetType === TargetType.Containers) {
    return generateContainerAttackConfigurationTargets(target)
  }

  // Return Kubernetes targets by default
  return generateKubernetesAttackConfigurationTargets(target, impact, targets)
}

export function generateAttackConfigurationCommand(impact, metadata) {
  const metadataForCommand: MetadataEntry = metadata?.find(
    ({ command: commandType }) => commandType === impact.type
  )

  const infraCommandArgs = cliArgsToInfraCommandArgs({
    command: impact.type,
    args: impact.args,
    attackCategory: impact.category,
    providers: impact.providers,
    trafficImpactMapping: impact.traffic_impact_mapping,
    metadata
  })

  const command: SnakeCaseAttackCommandConfiguration = {
    infraCommandType: impact.type,
    infraCommandArgs: {
      ...infraCommandArgs,
      cliArgs: [
        impact.type,
        ...(impact?.args?.map((arg) => arg.toString()) ?? [])
      ]
    }
  }

  if (metadataForCommand?.type === 'Network') {
    command.infraCommandArgs.providers = impact.providers
    if (impact.traffic_impact_mapping) {
      command.infraCommandArgs.traffic_impact_mapping =
        impact.traffic_impact_mapping
    }
  }

  return command
}

export function generateAttackConfiguration(
  targetType: TargetType,
  { target, impact },
  metadata,
  targets
): AttackConfiguration {
  const command: AttackCommandConfiguration =
    generateAttackConfigurationCommand(impact, metadata)

  const targetingConfiguration: AttackTargetingConfiguration =
    generateAttackConfigurationTargets(targetType, target, impact, targets)

  const sampling: AttackSamplingConfiguration =
    generateAttackConfigurationSampling(target)

  const attackConfiguration: AttackConfiguration = {
    command,
    targets: targetingConfiguration,
    sampling
  }

  return attackConfiguration
}

/**
 * Get the list of OSes that are not supported by the selected argument
 */
export function getUnsupportedOSListForArgument(
  selectedArgument: CommandArg,
  clientsOSList: string[]
) {
  if (selectedArgument && clientsOSList) {
    return clientsOSList.filter(
      (v) => !Object.keys(selectedArgument.versions).includes(v)
    )
  }

  return []
}

export function getNextTargetParams(
  newValue:
    | InfraTargetingAutoCompleteSearchOption
    | InfraTargetingAutoCompleteSearchOption[]
) {
  const nextTargetParams = {}

  if (Array.isArray(newValue)) {
    newValue.forEach((val) => {
      const { paramKey, paramValue } = val.value

      nextTargetParams[paramKey] =
        nextTargetParams[paramKey] || new Set<string>()
      nextTargetParams[paramKey].add(paramValue)
    })
  } else {
    const { paramKey, paramValue } = newValue.value

    nextTargetParams[paramKey] = nextTargetParams[paramKey] || new Set<string>()
    nextTargetParams[paramKey].add(paramValue)
  }

  return nextTargetParams
}
